import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { Button } from '../../components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { toast } from 'sonner';
import { 
  Plus, Trash2, BookOpen, Globe, GraduationCap, Search, 
  Building2, HelpCircle, FileText, Shield, Upload, Edit2, Save
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const CATEGORIES = [
  { id: 'general', label: 'General', icon: Globe, description: 'Información general de IAASP' },
  { id: 'services', label: 'Servicios', icon: Shield, description: 'Productos y servicios ofrecidos' },
  { id: 'courses', label: 'Cursos', icon: GraduationCap, description: 'Información de cursos específicos' },
  { id: 'faq', label: 'FAQ', icon: HelpCircle, description: 'Preguntas frecuentes' },
  { id: 'policies', label: 'Políticas', icon: FileText, description: 'Políticas y procedimientos' },
  { id: 'sponsors', label: 'Patrocinadores', icon: Building2, description: 'Información de patrocinadores' },
];

export default function AIKnowledgeManager() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [knowledge, setKnowledge] = useState([]);
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [activeCategory, setActiveCategory] = useState('all');
  const [showBulkImport, setShowBulkImport] = useState(false);
  const [bulkText, setBulkText] = useState('');
  const [formData, setFormData] = useState({
    title: '',
    content: '',
    scope: 'general',
    category: 'general',
    course_id: '',
    visibility: 'public'
  });

  useEffect(() => {
    if (!user || !['superadmin', 'admin'].includes(user.role)) {
      navigate('/dashboard');
      return;
    }
    loadData();
  }, [user, navigate]);

  const loadData = async () => {
    const token = localStorage.getItem('token');
    try {
      const [knowledgeRes, coursesRes] = await Promise.all([
        fetch(`${API_URL}/api/ai/knowledge`, {
          headers: { 'Authorization': `Bearer ${token}` }
        }),
        fetch(`${API_URL}/api/courses`)
      ]);

      if (knowledgeRes.ok) {
        const data = await knowledgeRes.json();
        setKnowledge(data);
      }

      if (coursesRes.ok) {
        const data = await coursesRes.json();
        setCourses(data);
      }
    } catch (error) {
      toast.error('Error al cargar datos');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const token = localStorage.getItem('token');

    try {
      const payload = {
        ...formData,
        course_id: formData.scope === 'course' ? formData.course_id : null
      };

      const url = editingId 
        ? `${API_URL}/api/ai/knowledge/${editingId}`
        : `${API_URL}/api/ai/knowledge`;
      
      const method = editingId ? 'PUT' : 'POST';

      const res = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(payload)
      });

      if (res.ok) {
        toast.success(editingId ? 'Conocimiento actualizado' : 'Conocimiento agregado');
        setShowForm(false);
        setEditingId(null);
        setFormData({ title: '', content: '', scope: 'general', category: 'general', course_id: '', visibility: 'public' });
        loadData();
      } else {
        toast.error('Error al guardar');
      }
    } catch (error) {
      toast.error('Error de conexión');
    }
  };

  const handleEdit = (item) => {
    setFormData({
      title: item.title,
      content: item.content,
      scope: item.scope || 'general',
      category: item.category || 'general',
      course_id: item.course_id || '',
      visibility: item.visibility || 'public'
    });
    setEditingId(item.id);
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('¿Eliminar este conocimiento?')) return;
    
    const token = localStorage.getItem('token');
    try {
      const res = await fetch(`${API_URL}/api/ai/knowledge/${id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (res.ok) {
        toast.success('Eliminado');
        setKnowledge(prev => prev.filter(k => k.id !== id));
      }
    } catch (error) {
      toast.error('Error al eliminar');
    }
  };

  const handleBulkImport = async () => {
    if (!bulkText.trim()) {
      toast.error('Ingresa el texto a importar');
      return;
    }

    const token = localStorage.getItem('token');
    
    // Dividir por líneas dobles o por títulos que empiecen con #
    const sections = bulkText.split(/\n\n+/).filter(s => s.trim());
    
    let imported = 0;
    for (const section of sections) {
      const lines = section.split('\n');
      const title = lines[0].replace(/^#+\s*/, '').trim();
      const content = lines.slice(1).join('\n').trim();
      
      if (title && content) {
        try {
          const res = await fetch(`${API_URL}/api/ai/knowledge`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${token}`
            },
            body: JSON.stringify({
              title,
              content,
              scope: 'general',
              category: 'general',
              visibility: 'public'
            })
          });
          if (res.ok) imported++;
        } catch (e) {
          console.error('Error importing section:', title);
        }
      }
    }

    toast.success(`${imported} secciones importadas`);
    setBulkText('');
    setShowBulkImport(false);
    loadData();
  };

  const filteredKnowledge = knowledge.filter(k => {
    const matchesSearch = k.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
      k.content.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = activeCategory === 'all' || k.category === activeCategory;
    return matchesSearch && matchesCategory;
  });

  const getCategoryIcon = (category) => {
    const cat = CATEGORIES.find(c => c.id === category);
    return cat ? cat.icon : Globe;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-muted/30 flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-accent"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-muted/30 py-8" data-testid="ai-knowledge-page">
      <div className="max-w-7xl mx-auto px-6">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8">
          <div>
            <h1 className="text-2xl font-bold text-foreground">Base de Conocimiento AI</h1>
            <p className="text-muted-foreground text-sm mt-1">
              Gestiona la información que el tutor virtual utiliza para responder
            </p>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => setShowBulkImport(true)} className="gap-2">
              <Upload className="w-4 h-4" />
              Importar
            </Button>
            <Button onClick={() => { setEditingId(null); setFormData({ title: '', content: '', scope: 'general', category: 'general', course_id: '', visibility: 'public' }); setShowForm(true); }} className="gap-2" data-testid="add-knowledge-btn">
              <Plus className="w-4 h-4" />
              Agregar
            </Button>
          </div>
        </div>

        {/* Search and Categories */}
        <div className="flex flex-col md:flex-row gap-4 mb-6">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <input
              type="text"
              placeholder="Buscar en la base de conocimiento..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2.5 rounded-lg border bg-background focus:outline-none focus:ring-2 focus:ring-accent/50"
              data-testid="search-knowledge"
            />
          </div>
        </div>

        {/* Category Tabs */}
        <Tabs value={activeCategory} onValueChange={setActiveCategory} className="mb-6">
          <TabsList className="flex-wrap h-auto gap-1">
            <TabsTrigger value="all" className="text-xs">Todos ({knowledge.length})</TabsTrigger>
            {CATEGORIES.map(cat => {
              const count = knowledge.filter(k => k.category === cat.id).length;
              return (
                <TabsTrigger key={cat.id} value={cat.id} className="text-xs gap-1">
                  <cat.icon className="w-3 h-3" />
                  {cat.label} ({count})
                </TabsTrigger>
              );
            })}
          </TabsList>
        </Tabs>

        {/* Bulk Import Modal */}
        {showBulkImport && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-background rounded-xl p-6 w-full max-w-3xl max-h-[90vh] overflow-y-auto">
              <h2 className="text-xl font-semibold mb-4">Importar Conocimiento Masivo</h2>
              <p className="text-sm text-muted-foreground mb-4">
                Pega texto con secciones separadas por líneas en blanco. La primera línea de cada sección será el título.
              </p>
              <textarea
                value={bulkText}
                onChange={(e) => setBulkText(e.target.value)}
                className="w-full px-3 py-2 rounded-lg border bg-background focus:outline-none focus:ring-2 focus:ring-accent/50 min-h-[300px] font-mono text-sm"
                placeholder={`# Título de la sección 1
Contenido de la primera sección...

# Título de la sección 2
Contenido de la segunda sección...`}
              />
              <div className="flex gap-3 pt-4">
                <Button type="button" variant="outline" onClick={() => setShowBulkImport(false)} className="flex-1">
                  Cancelar
                </Button>
                <Button onClick={handleBulkImport} className="flex-1">
                  Importar
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* Form Modal */}
        {showForm && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-background rounded-xl p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
              <h2 className="text-xl font-semibold mb-4">
                {editingId ? 'Editar Conocimiento' : 'Agregar Conocimiento'}
              </h2>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium mb-1">Título</label>
                  <input
                    type="text"
                    value={formData.title}
                    onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                    className="w-full px-3 py-2 rounded-lg border bg-background focus:outline-none focus:ring-2 focus:ring-accent/50"
                    required
                    data-testid="knowledge-title"
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium mb-1">Categoría</label>
                    <select
                      value={formData.category}
                      onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                      className="w-full px-3 py-2 rounded-lg border bg-background focus:outline-none focus:ring-2 focus:ring-accent/50"
                      data-testid="knowledge-category"
                    >
                      {CATEGORIES.map(cat => (
                        <option key={cat.id} value={cat.id}>{cat.label} - {cat.description}</option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium mb-1">Alcance</label>
                    <select
                      value={formData.scope}
                      onChange={(e) => setFormData({ ...formData, scope: e.target.value })}
                      className="w-full px-3 py-2 rounded-lg border bg-background focus:outline-none focus:ring-2 focus:ring-accent/50"
                      data-testid="knowledge-scope"
                    >
                      <option value="general">General (disponible para todos)</option>
                      <option value="course">Específico de un curso</option>
                    </select>
                  </div>
                </div>

                {formData.scope === 'course' && (
                  <div>
                    <label className="block text-sm font-medium mb-1">Curso</label>
                    <select
                      value={formData.course_id}
                      onChange={(e) => setFormData({ ...formData, course_id: e.target.value })}
                      className="w-full px-3 py-2 rounded-lg border bg-background focus:outline-none focus:ring-2 focus:ring-accent/50"
                      required
                      data-testid="knowledge-course"
                    >
                      <option value="">Seleccionar curso...</option>
                      {courses.map(course => (
                        <option key={course.id} value={course.id}>{course.title}</option>
                      ))}
                    </select>
                  </div>
                )}

                <div>
                  <label className="block text-sm font-medium mb-1">Contenido</label>
                  <textarea
                    value={formData.content}
                    onChange={(e) => setFormData({ ...formData, content: e.target.value })}
                    className="w-full px-3 py-2 rounded-lg border bg-background focus:outline-none focus:ring-2 focus:ring-accent/50 min-h-[200px]"
                    placeholder="Escribe aquí el contenido que el AI debe conocer..."
                    required
                    data-testid="knowledge-content"
                  />
                  <p className="text-xs text-muted-foreground mt-1">
                    Tip: Sé específico y detallado. Incluye preguntas comunes y sus respuestas.
                  </p>
                </div>

                <div className="flex gap-3 pt-4">
                  <Button type="button" variant="outline" onClick={() => { setShowForm(false); setEditingId(null); }} className="flex-1">
                    Cancelar
                  </Button>
                  <Button type="submit" className="flex-1 gap-2" data-testid="save-knowledge-btn">
                    <Save className="w-4 h-4" />
                    {editingId ? 'Actualizar' : 'Guardar'}
                  </Button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Knowledge Grid */}
        {filteredKnowledge.length === 0 ? (
          <div className="text-center py-16 bg-background rounded-xl border">
            <BookOpen className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
            <h3 className="text-lg font-medium mb-2">No hay conocimiento registrado</h3>
            <p className="text-muted-foreground text-sm mb-4">
              Agrega contenido para que el tutor virtual pueda responder mejor
            </p>
            <Button onClick={() => setShowForm(true)} variant="outline">
              Agregar primer conocimiento
            </Button>
          </div>
        ) : (
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
            {filteredKnowledge.map((item) => {
              const CategoryIcon = getCategoryIcon(item.category);
              return (
                <div
                  key={item.id}
                  className="bg-background rounded-xl border p-5 hover:shadow-md transition-shadow"
                  data-testid={`knowledge-${item.id}`}
                >
                  <div className="flex items-start justify-between gap-3 mb-3">
                    <div className="flex items-center gap-2">
                      <CategoryIcon className="w-4 h-4 text-accent" />
                      <h3 className="font-semibold text-foreground line-clamp-1">{item.title}</h3>
                    </div>
                    <div className="flex gap-1">
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => handleEdit(item)}
                        className="text-muted-foreground hover:text-accent h-8 w-8"
                      >
                        <Edit2 className="w-4 h-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => handleDelete(item.id)}
                        className="text-muted-foreground hover:text-destructive h-8 w-8"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                  
                  <p className="text-sm text-muted-foreground line-clamp-3 mb-3">
                    {item.content}
                  </p>
                  
                  <div className="flex items-center gap-2 text-xs text-muted-foreground flex-wrap">
                    <span className={`px-2 py-0.5 rounded-full ${
                      item.scope === 'general' ? 'bg-blue-100 text-blue-700' : 'bg-green-100 text-green-700'
                    }`}>
                      {item.scope === 'general' ? 'General' : 'Curso'}
                    </span>
                    <span className="px-2 py-0.5 rounded-full bg-muted">
                      {CATEGORIES.find(c => c.id === item.category)?.label || 'General'}
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
